import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectGroup,
    SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import apiClient from "../../../utils/apiClient";
import { Input } from "../../../components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
    { value: "active", label: "active" },
    { value: "inactive", label: "inactive" },
    { value: "deactivated", label: "deactivated" },
    { value: "dummy", label: "dummy" },
];

const labelOptions = [
    { value: "clearmind", label: "clearmind" },
    { value: "satyam", label: "satyam" },
];

export function DhanSSXTSUserForm({ handleAddUserIndexMultiplier, onClose, proxyData }) {
    const [clientId, setClientId] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [status, setStatus] = useState("");
    const [apiAppKey, setApiAppKey] = useState("");
    const [apiAppSecret, setApiAppSecret] = useState("");
    const [label, setLabel] = useState("");
    const [type, setType] = useState(""); // Default empty, user must select

    // Multiplier states
    const [multiplier, setMultiplier] = useState(1);
    const [bankniftyMultiplier, setBankniftyMultiplier] = useState(0);
    const [finniftyMultiplier, setFinniftyMultiplier] = useState(0);
    const [midcpniftyMultiplier, setMidcpniftyMultiplier] = useState(0);
    const [sensexMultiplier, setSensexMultiplier] = useState(0);



    // Visibility states
    const [showPassword, setShowPassword] = useState(false);
    const [showApiAppKey, setShowApiAppKey] = useState(false);
    const [showApiAppSecret, setShowApiAppSecret] = useState(false);


    const handleAddDhanXTSUser = () => {
        const data = {
            client_id: clientId.trim(),
            username: username.trim(),
            password: password.trim(),
            status: status.trim(),
            api_app_key: apiAppKey.trim(),
            api_app_secret: apiAppSecret.trim(),
            label: label.trim(),
            type: type.trim(),
        };

        if (
            !data.client_id ||
            !data.username ||
            !data.password ||
            !data.status ||
            !data.api_app_key ||
            !data.api_app_secret ||
            !data.label ||
            !data.type ||
            (multiplier === "" || multiplier === null) ||
            (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
            (finniftyMultiplier === "" || finniftyMultiplier === null) ||
            (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
            (sensexMultiplier === "" || sensexMultiplier === null)
        ) {
            console.log("Missing required fields:", data);
            toast.error("Missing required fields !!", {
                duration: 5000,
            });
            return;
        }

        console.log("New Dhan XTS User Data", data);

        let config = {
            method: "post",
            url: `${server.godseye.baseUrl}add-dhan-xts-user`,
            headers: {
                "Content-Type": "application/json",
            },
            data: JSON.stringify(data),
        };

        apiClient
            .request(config)
            .then((response) => {
                console.log("Server response:", response.data);
                if (response.data.status === 200) {
                    toast.success("New Dhan XTS user added successfully!", {
                        duration: 5000,
                    });
                    // Call the parent function to handle multipliers (using username or client_id as name?)
                    // Nubra form used 'name' state, here we have 'username'
                    // handleAddUserIndexMultiplier(clientId, username, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier);
                    onClose(true);
                } else {
                    onClose(false);
                }
            })
            .catch((error) => {
                console.error("Failed to add new Dhan XTS user", error);
                toast.error("Failed to add user");
                onClose(false);
            });
    };

    return (
        <div className="font-inter space-y-4">
            <div className="grid grid-cols-4 gap-6 py-4">
                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Client Id <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Username <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Status <span className="text-red-500">*</span>
                    </Label>
                    <Select value={status} onValueChange={(val) => setStatus(val)}>
                        <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                            <SelectValue placeholder="Select Status" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-700">
                            <SelectGroup>
                                {statusOptions.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        API App Key <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showApiAppKey ? "text" : "password"}
                            value={apiAppKey}
                            onChange={(e) => setApiAppKey(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiAppKey(!showApiAppKey)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showApiAppKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        API App Secret <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                        <Input
                            className="text-white pr-10"
                            type={showApiAppSecret ? "text" : "password"}
                            value={apiAppSecret}
                            onChange={(e) => setApiAppSecret(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => setShowApiAppSecret(!showApiAppSecret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                        >
                            {showApiAppSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                    </div>
                </div>



                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Label <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        className="text-white"
                        type="text"
                        list="label-options"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Select or type label"
                    />
                    <datalist id="label-options">
                        {labelOptions.map((l) => (
                            <option key={l.value} value={l.value} />
                        ))}
                    </datalist>
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">
                        Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={type} onValueChange={(val) => setType(val)}>
                        <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                            <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-700">
                            <SelectGroup>
                                <SelectItem value="retail">Retail</SelectItem>
                                <SelectItem value="fpi">FPI</SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                {/* Multiplier Fields */}
                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Multiplier Number <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={multiplier}
                        onChange={(e) => setMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Bank Nifty Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={bankniftyMultiplier}
                        onChange={(e) => setBankniftyMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Fin Nifty Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={finniftyMultiplier}
                        onChange={(e) => setFinniftyMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Midcap Nifty Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={midcpniftyMultiplier}
                        onChange={(e) => setMidcpniftyMultiplier(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                    <Label className="text-white">Sensex Multiplier <span className="text-red-500">*</span></Label>
                    <Input
                        className="text-white"
                        type="number"
                        value={sensexMultiplier}
                        onChange={(e) => setSensexMultiplier(e.target.value)}
                    />
                </div>

            </div>

            <div className="mt-6 flex justify-end">
                <Button
                    disabled={
                        !clientId ||
                        !username ||
                        !password ||
                        !status ||
                        !apiAppKey ||
                        !apiAppSecret ||
                        !label ||
                        !type
                    }
                    type="submit"
                    className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
                    onClick={handleAddDhanXTSUser}
                >
                    Add Dhan XTS User
                </Button>
            </div>
        </div>
    );
}
